import PillarLayout from '../components/PillarLayout'

export default function ControlHorarioEmpresas() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Control horario para empresas en España: guía 2026",
    "description": "Cómo implantar un sistema de control horario en tu empresa cumpliendo el RD-Ley 8/2019. Métodos, requisitos y soluciones para pymes.",
    "author": { "@type": "Organization", "name": "PresenzIA" },
    "publisher": { "@type": "Organization", "name": "PresenzIA", "url": "https://presenzia.app" },
    "datePublished": "2026-05-03",
    "dateModified": "2026-05-03",
    "mainEntityOfPage": "https://presenzia.app/control-horario-empresas",
    "inLanguage": "es"
  }

  return (
    <PillarLayout
      title="Control horario para empresas en España: guía completa 2026"
      description="Cómo implantar un sistema de control horario en tu empresa cumpliendo el RD-Ley 8/2019. Métodos legales, requisitos técnicos, sanciones y la mejor solución para pymes."
      canonicalPath="/control-horario-empresas"
      jsonLd={jsonLd}
    >
      <h1>Control horario para empresas en España: guía completa 2026</h1>
      <p className="lead">
        El control horario dejó de ser opcional en España en 2019. Hoy, cualquier empresa con
        empleados está obligada a llevar un registro diario de la jornada de cada trabajador.
        Esta guía te explica qué exige la ley, cómo implantarlo bien y por qué un sistema
        digital es la única opción razonable a partir de 2026.
      </p>

      <h2>¿Qué empresas están obligadas?</h2>
      <p>
        Todas. Desde la entrada en vigor del Real Decreto-ley 8/2019, cualquier empresa con
        trabajadores por cuenta ajena —desde la microempresa de 1 empleado hasta la gran
        corporación— debe registrar la jornada laboral diaria. La obligación se extiende a:
      </p>
      <ul>
        <li>Empleados a tiempo completo y a tiempo parcial.</li>
        <li>Trabajadores en oficina, en remoto y en desplazamiento.</li>
        <li>Personal con jornada partida o continua.</li>
        <li>Empleados con horario flexible o por turnos.</li>
      </ul>
      <p>
        Sólo quedan fuera los relevos del personal de alta dirección, autónomos y socios
        trabajadores en sociedades laborales en determinados supuestos.
      </p>

      <h2>Qué debe contener el registro horario</h2>
      <p>El registro debe permitir acreditar, como mínimo:</p>
      <ul>
        <li><strong>Hora exacta de inicio</strong> de la jornada laboral.</li>
        <li><strong>Hora exacta de finalización</strong> de la jornada.</li>
        <li><strong>Identificación inequívoca del trabajador</strong>.</li>
        <li><strong>Trazabilidad e integridad:</strong> los registros no deben ser
          modificables sin dejar huella de auditoría.</li>
        <li><strong>Conservación durante 4 años</strong>, accesible para el trabajador,
          sus representantes y la Inspección de Trabajo.</li>
      </ul>
      <p>
        El RD-Ley 8/2019 no impone un sistema técnico concreto, pero la jurisprudencia y la
        ITSS rechazan sistemáticamente registros en papel firmados a posteriori, hojas de
        cálculo manipulables o emails, porque no garantizan integridad.
      </p>

      <h2>Métodos de fichaje admitidos en 2026</h2>
      <p>
        El nuevo Real Decreto sobre control horario digital obligatorio (en tramitación
        durante 2026) refuerza la exigencia de medios digitales. Los métodos válidos en la
        práctica son:
      </p>
      <ul>
        <li><strong>App móvil</strong> con autenticación del empleado (PIN, biometría, OAuth).</li>
        <li><strong>Web del empleado</strong> con login seguro.</li>
        <li><strong>Quiosco compartido</strong> con identificación por PIN o tarjeta.</li>
        <li><strong>QR físico en el centro de trabajo</strong>, escaneado desde la app.</li>
        <li><strong>Geolocalización</strong> para trabajos en ruta o por proyectos.</li>
        <li><strong>Lectores biométricos</strong> (con base legal y consentimiento expreso).</li>
      </ul>
      <p>
        Lo que <strong>nunca se acepta</strong> en una inspección: hojas de papel, planillas
        Excel sin trazabilidad, registros firmados al final de mes, o sistemas que no
        identifiquen unívocamente al trabajador.
      </p>

      <h2>Sanciones por no cumplir</h2>
      <p>
        El incumplimiento del registro horario es una infracción grave del artículo 7.5 LISOS,
        sancionable con multas de:
      </p>
      <ul>
        <li><strong>Leve:</strong> 70 € a 750 €.</li>
        <li><strong>Grave:</strong> 751 € a 7.500 € por cada centro de trabajo.</li>
        <li><strong>Muy grave (reincidencia o falsedad documental):</strong> 7.501 € a 187.515 €.</li>
      </ul>
      <p>
        Importante: la multa se aplica <em>por centro de trabajo</em>, no por empresa. Una
        cadena con 4 oficinas que no fichen puede recibir 4 sanciones simultáneas.
      </p>

      <h2>Cómo implantar el control horario en tu empresa, paso a paso</h2>
      <ol>
        <li>
          <strong>Negocia con la representación de los trabajadores.</strong> El RD obliga
          a la negociación colectiva o, en su defecto, a la consulta con los representantes.
          Documenta esa negociación.
        </li>
        <li>
          <strong>Elige un sistema digital con auditoría.</strong> Que sea inmutable, con
          marca de tiempo del servidor (no del dispositivo) y registro de modificaciones.
        </li>
        <li>
          <strong>Define el método de fichaje.</strong> App, web, quiosco, QR. Lo más
          habitual en pymes es app + web con PIN.
        </li>
        <li>
          <strong>Comunica a la plantilla por escrito.</strong> Política interna firmada
          que explique el sistema, dónde fichar, cómo corregir errores y dónde consultar
          su registro.
        </li>
        <li>
          <strong>Configura horarios y excepciones</strong> (festivos, vacaciones, ausencias
          justificadas, hospitales, formación).
        </li>
        <li>
          <strong>Forma a los managers</strong> para validar fichajes, autorizar correcciones
          y aprobar horas extras.
        </li>
        <li>
          <strong>Conserva los registros 4 años.</strong> El sistema debe permitir extraer
          informes individuales y agregados a petición de la ITSS.
        </li>
      </ol>

      <h2>Errores frecuentes que hemos visto en inspecciones</h2>
      <ul>
        <li><strong>Excel "actualizado a final de mes":</strong> insuficiente. Necesita fichaje en tiempo real.</li>
        <li><strong>No registrar las pausas:</strong> sólo entrada/salida no es válido para jornadas largas.</li>
        <li><strong>Falta de identificación inequívoca:</strong> hojas firmadas con un apellido no acreditan quién fichó.</li>
        <li><strong>Registros que se pueden editar sin huella:</strong> rompen la integridad probatoria.</li>
        <li><strong>No documentar la negociación con los representantes:</strong> motivo añadido de sanción.</li>
      </ul>

      <h2>Por qué PresenzIA es la opción para pymes</h2>
      <p>
        PresenzIA es un software de control horario diseñado específicamente para pymes,
        micropymes y asesorías españolas:
      </p>
      <ul>
        <li><strong>Cumple el RD-Ley 8/2019</strong> desde el primer día con auditoría completa.</li>
        <li><strong>Desde 1,50 €/empleado/mes</strong> sin mínimos.</li>
        <li><strong>Servidores en la UE</strong> y RGPD compliant.</li>
        <li>App móvil, web, fichaje grupal, geolocalización y QR físico.</li>
        <li>Reportes en PDF firmados listos para la ITSS.</li>
        <li>Gestión de ausencias, vacaciones y horas extras integrada.</li>
        <li>14 días gratis sin tarjeta. Onboarding asistido sin coste; migración del histórico bajo presupuesto.</li>
      </ul>
    </PillarLayout>
  )
}
